// -----------------------------------------------------------------------------
// Config feature — DTOs + fetcher helpers (Phase 5 Wave 4, agent-ui-config).
//
// Local to this feature so we don't trample src/api/types.ts (owned by the
// shell). Mirrors the C# DTOs in:
//   - DocuSense.Application.Dtos.TenantModelProviders.*
//   - DocuSense.Application.Dtos.Webhooks.*
//   - DocuSense.Application.UseCases.Licensing.LicenseStatusResponse
//
// NOTE: The shell's src/api/types.ts exposes a `LicenseStatusResponse` type
// that does not match the actual backend payload (different field names). We
// re-declare the correct shape here, scoped to the config feature, rather than
// touching the shared module.
// -----------------------------------------------------------------------------

import { apiFetch } from '../../api/client';
import { Endpoints } from '../../api/types';

// ---------- Provider enums (mirror Domain/Enums/ProviderType.cs) ----------

export const ProviderTypes = {
  AnthropicCloud: 'AnthropicCloud',
  OpenAiCloud: 'OpenAiCloud',
  AzureOpenAi: 'AzureOpenAi',
  OllamaLocal: 'OllamaLocal',
  ContentUnderstanding: 'ContentUnderstanding',
  OllamaViaAzureRelay: 'OllamaViaAzureRelay',
} as const;
export type ProviderType = (typeof ProviderTypes)[keyof typeof ProviderTypes];

export const PROVIDER_TYPE_OPTIONS: ReadonlyArray<{
  value: ProviderType;
  label: string;
  /** True when the type requires an API key in plaintext. */
  requiresApiKey: boolean;
  /** True when the type requires an endpoint URL. */
  requiresEndpoint: boolean;
  /** True when the type is gated by a paid plan (Professional+). */
  isLocalAi: boolean;
}> = [
  { value: 'AnthropicCloud', label: 'Anthropic Cloud', requiresApiKey: true, requiresEndpoint: false, isLocalAi: false },
  { value: 'OpenAiCloud', label: 'OpenAI Cloud', requiresApiKey: true, requiresEndpoint: false, isLocalAi: false },
  { value: 'AzureOpenAi', label: 'Azure OpenAI', requiresApiKey: true, requiresEndpoint: true, isLocalAi: false },
  { value: 'OllamaLocal', label: 'Ollama Local', requiresApiKey: false, requiresEndpoint: true, isLocalAi: true },
  { value: 'ContentUnderstanding', label: 'Azure Content Understanding', requiresApiKey: true, requiresEndpoint: true, isLocalAi: false },
  { value: 'OllamaViaAzureRelay', label: 'Ollama via Azure Relay', requiresApiKey: true, requiresEndpoint: true, isLocalAi: true },
];

export function getProviderTypeMeta(type: ProviderType): (typeof PROVIDER_TYPE_OPTIONS)[number] {
  const match = PROVIDER_TYPE_OPTIONS.find((option) => option.value === type);
  // Fall back to the most permissive shape if the backend returns a type we don't know yet.
  // The first option is statically guaranteed to exist; the non-null assertion satisfies
  // noUncheckedIndexedAccess without changing runtime behaviour.
  return match ?? (PROVIDER_TYPE_OPTIONS[0] as (typeof PROVIDER_TYPE_OPTIONS)[number]);
}

// ---------- Provider DTOs ----------

/**
 * Inbound representation of a custom HTTP header on a provider. When `isSecret = true`,
 * `value` is plaintext meant for Key Vault and is never persisted in Cosmos.
 */
export interface ProviderHeaderInput {
  name: string;
  value: string;
  isSecret: boolean;
}

/**
 * Outbound projection. For secret entries, `value` is the Key Vault secret name (never the
 * credential itself).
 */
export interface ProviderHeaderView {
  name: string;
  value: string;
  isSecret: boolean;
}

export interface ModelProviderResponse {
  providerId: string;
  /** ProviderType enum name (PascalCase). */
  type: string;
  alias: string;
  endpoint?: string | null;
  modelName?: string | null;
  /** Content Understanding only: completion-model deployment name. */
  completionDeployment?: string | null;
  /** Content Understanding only: embedding-model deployment name. */
  embeddingDeployment?: string | null;
  isActive: boolean;
  /** Null when the tenant did not override the extraction-path token ceiling. */
  maxTokensForExtraction?: number | null;
  /** Null when the tenant did not override the discovery-path token ceiling. */
  maxTokensForDiscovery?: number | null;
  customHeaders: ProviderHeaderView[];
}

export interface AddModelProviderRequest {
  type: ProviderType;
  alias: string;
  endpoint?: string | null;
  modelName?: string | null;
  /** Content Understanding only: completion-model deployment name (e.g. gpt-4.1). */
  completionDeployment?: string | null;
  /** Content Understanding only: embedding-model deployment name (e.g. text-embedding-3-large). */
  embeddingDeployment?: string | null;
  /** Required for cloud providers; ignored for Ollama. */
  apiKeyPlaintext?: string | null;
  /** Optional override; if omitted the activity default applies. */
  maxTokensForExtraction?: number | null;
  /** Optional override; if omitted the activity default applies. */
  maxTokensForDiscovery?: number | null;
  customHeaders?: ProviderHeaderInput[];
}

export interface UpdateModelProviderRequest {
  /** Route value — kept here for clarity, not serialized into the body. */
  providerId: string;
  alias?: string | null;
  endpoint?: string | null;
  modelName?: string | null;
  /** Content Understanding only: new completion-model deployment name, or null to keep current. */
  completionDeployment?: string | null;
  /** Content Understanding only: new embedding-model deployment name, or null to keep current. */
  embeddingDeployment?: string | null;
  /** Pass a non-empty string to rotate the secret; omit/null to leave it untouched. */
  newApiKeyPlaintext?: string | null;
  isActive?: boolean | null;
  /** Tri-state: null = no change, 0 = clear override, positive = set new ceiling. */
  maxTokensForExtraction?: number | null;
  /** Tri-state: null = no change, 0 = clear override, positive = set new ceiling. */
  maxTokensForDiscovery?: number | null;
  /** Non-null replaces the whole list; empty array clears every header. */
  customHeaders?: ProviderHeaderInput[];
}

/** UI defaults shown as placeholders when the tenant has no override. Must mirror the
 *  activity constants in `ExtractWithAiActivity` and `AnalyzeCandidateFieldsActivity`. */
export const PROVIDER_MAX_TOKENS_DEFAULTS = {
  extraction: 32768,
  discovery: 8192,
} as const;

/** Policy ceiling enforced by the validator (mirrors TenantModelProviderValidationConstants). */
export const PROVIDER_MAX_TOKENS_CEILING = 200_000;

export interface TestModelProviderResponse {
  success: boolean;
  latencyMs: number;
  model?: string | null;
  errorMessage?: string | null;
}

// ---------- Webhook DTOs ----------

/** Wire-format event names — must match WebhookEventFormatter on the backend. */
export const WEBHOOK_EVENTS = [
  'document.completed',
  'document.failed',
  'document.review_required',
] as const;
export type WebhookEventName = (typeof WEBHOOK_EVENTS)[number];

export const WEBHOOK_EVENT_LABELS: Record<WebhookEventName, string> = {
  'document.completed': 'Documento completado',
  'document.failed': 'Documento fallido',
  'document.review_required': 'Requiere revisión',
};

export interface WebhookSubscriptionResponse {
  id: string;
  url: string;
  events: string[];
  /** Document-type slugs this subscription is restricted to. Empty = all types. */
  documentTypeSlugs: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastDeliveryAt?: string | null;
  /** `Success` | `Failed` | `DeadLettered`. Null when never attempted. */
  lastDeliveryStatus?: string | null;
}

export interface CreateWebhookSubscriptionRequest {
  url: string;
  events: WebhookEventName[];
  /** Omit or empty = all types. */
  documentTypeSlugs?: string[];
}

export interface CreateWebhookSubscriptionResponse {
  id: string;
  url: string;
  events: string[];
  documentTypeSlugs: string[];
  isActive: boolean;
  createdAt: string;
  /** Returned exactly once on creation; cannot be retrieved afterwards. */
  secret: string;
}

export interface UpdateWebhookSubscriptionRequest {
  id: string;
  url?: string | null;
  events?: WebhookEventName[] | null;
  isActive?: boolean | null;
  /** null = leave untouched; [] = clear (all types); non-empty = replace. */
  documentTypeSlugs?: string[] | null;
}

export interface RotateWebhookSecretResponse {
  id: string;
  rotatedAt: string;
  /** New plaintext secret — surfaced exactly once. */
  secret: string;
}

export interface TestWebhookSubscriptionResponse {
  deliveryEnqueued: boolean;
  message: string;
}

// ---------- License DTOs (mirrors LicenseStatusResponse C# record) ----------

export interface LicenseFeaturesResponse {
  /** Null on Enterprise (unlimited). */
  documentTypesLimit: number | null;
  allowsLocalAiProviders: boolean;
  allowsWebhooks: boolean;
  /** Pre-formatted retention window (e.g. "30 days"). */
  jobRetention: string;
}

export interface ConfigLicenseStatusResponse {
  licenseType: string;
  documentsUsed: number;
  /** Null on Enterprise (unlimited). */
  documentsLimit: number | null;
  resetsAt: string;
  features: LicenseFeaturesResponse;
}

// ---------- Fetchers — Providers ----------

export function listProviders(signal?: AbortSignal): Promise<ModelProviderResponse[]> {
  return apiFetch<ModelProviderResponse[]>(
    Endpoints.connections,
    signal !== undefined ? { signal } : {},
  );
}

export function addProvider(request: AddModelProviderRequest): Promise<ModelProviderResponse> {
  return apiFetch<ModelProviderResponse>(Endpoints.connections, {
    method: 'POST',
    json: request,
  });
}

export function updateProvider(request: UpdateModelProviderRequest): Promise<ModelProviderResponse> {
  // The backend's UpdateModelProviderRequest carries ProviderId as a `required` field
  // even though the canonical source is the URL segment. The handler overwrites the
  // body value with the route value as an anti-mismatch guard, so sending the same id
  // in both places is intentional — stripping it from the body would fail JSON
  // deserialization against the required-property contract.
  return apiFetch<ModelProviderResponse>(
    `${Endpoints.connections}/${encodeURIComponent(request.providerId)}`,
    { method: 'PUT', json: request },
  );
}

export function deleteProvider(providerId: string): Promise<null> {
  return apiFetch<null>(`${Endpoints.connections}/${encodeURIComponent(providerId)}`, {
    method: 'DELETE',
  });
}

export function testProvider(providerId: string): Promise<TestModelProviderResponse> {
  return apiFetch<TestModelProviderResponse>(
    `${Endpoints.connections}/${encodeURIComponent(providerId)}/test`,
    { method: 'POST' },
  );
}

// ---------- Fetchers — Webhooks ----------

export function listWebhooks(signal?: AbortSignal): Promise<WebhookSubscriptionResponse[]> {
  return apiFetch<WebhookSubscriptionResponse[]>(
    Endpoints.webhooks,
    signal !== undefined ? { signal } : {},
  );
}

export function createWebhook(
  request: CreateWebhookSubscriptionRequest,
): Promise<CreateWebhookSubscriptionResponse> {
  return apiFetch<CreateWebhookSubscriptionResponse>(Endpoints.webhooks, {
    method: 'POST',
    json: request,
  });
}

export function updateWebhook(
  request: UpdateWebhookSubscriptionRequest,
): Promise<WebhookSubscriptionResponse> {
  const { id, ...body } = request;
  return apiFetch<WebhookSubscriptionResponse>(
    `${Endpoints.webhooks}/${encodeURIComponent(id)}`,
    { method: 'PUT', json: body },
  );
}

export function deleteWebhook(id: string): Promise<null> {
  return apiFetch<null>(`${Endpoints.webhooks}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function testWebhook(id: string): Promise<TestWebhookSubscriptionResponse> {
  return apiFetch<TestWebhookSubscriptionResponse>(
    `${Endpoints.webhooks}/${encodeURIComponent(id)}/test`,
    { method: 'POST' },
  );
}

export function rotateWebhookSecret(id: string): Promise<RotateWebhookSecretResponse> {
  return apiFetch<RotateWebhookSecretResponse>(
    `${Endpoints.webhooks}/${encodeURIComponent(id)}/rotate-secret`,
    { method: 'POST' },
  );
}

// ---------- Fetchers — License ----------

export function getLicenseStatus(signal?: AbortSignal): Promise<ConfigLicenseStatusResponse> {
  return apiFetch<ConfigLicenseStatusResponse>(
    Endpoints.licenseStatus,
    signal !== undefined ? { signal } : {},
  );
}

// ---------- Alert preferences (Phase B) ----------

export interface AlertRecipientResponse {
  address: string;
  displayName: string | null;
  state: string;
}

export interface AlertOptInResponse {
  alertType: string;
  optedIn: boolean;
}

export interface TenantAlertPreferencesResponse {
  recipients: AlertRecipientResponse[];
  optIns: AlertOptInResponse[];
}

export interface UpdateRecipientsBody {
  recipients: { address: string; displayName?: string | null }[];
}

export interface SetAlertOptInBody {
  alertType: string;
  optedIn: boolean;
}

export function getAlertPreferences(signal?: AbortSignal): Promise<TenantAlertPreferencesResponse> {
  return apiFetch<TenantAlertPreferencesResponse>(
    Endpoints.alertPreferences,
    signal !== undefined ? { signal } : {},
  );
}

export function updateAlertRecipients(body: UpdateRecipientsBody): Promise<TenantAlertPreferencesResponse> {
  return apiFetch<TenantAlertPreferencesResponse>(`${Endpoints.alertPreferences}/recipients`, {
    method: 'PUT',
    json: body,
  });
}

export function setAlertOptIn(body: SetAlertOptInBody): Promise<TenantAlertPreferencesResponse> {
  return apiFetch<TenantAlertPreferencesResponse>(`${Endpoints.alertPreferences}/opt-ins`, {
    method: 'PUT',
    json: body,
  });
}

// ---------- Branding (M1 — white-label) ----------

export interface TenantBrandingResponse {
  productName: string;
  primaryColorHex: string;
  logoUrl: string | null;
  secondaryColorHex: string | null;
  logoHeightPx: number;
  faviconUrl: string | null;
}

export interface UpdateBrandingBody {
  productName: string;
  primaryColorHex: string;
  logoUrl: string | null;
  secondaryColorHex: string | null;
  logoHeightPx: number;
  faviconUrl: string | null;
}

export function getBranding(signal?: AbortSignal): Promise<TenantBrandingResponse> {
  return apiFetch<TenantBrandingResponse>(Endpoints.branding, signal !== undefined ? { signal } : {});
}

export function updateBranding(body: UpdateBrandingBody): Promise<TenantBrandingResponse> {
  return apiFetch<TenantBrandingResponse>(Endpoints.branding, { method: 'PUT', json: body });
}

// ---------- Query keys ----------

export const ConfigQueryKeys = {
  providers: ['config', 'providers'] as const,
  webhooks: ['config', 'webhooks'] as const,
  license: ['config', 'license'] as const,
  alerts: ['config', 'alerts'] as const,
  branding: ['config', 'branding'] as const,
};
